<script setup lang="ts">
import type { FormattedSearchResult, SearchContext, WithinDocumentSearchStrategy } from '~/composables/document-search/strategies/types'
import { useSearchUI } from '~/composables/document-search/useSearchUI'

defineOptions({
  name: 'DocumentSearchUI',
})

const props = defineProps<{
  strategy: WithinDocumentSearchStrategy
  context: SearchContext
  searchMode?: boolean
}>()

const emit = defineEmits<{
  showSearchResult: [id: string, highlights: string[]]
}>()

// Create a reactive reference for searchMode that can be controlled externally
const searchModeRef = ref(false)
// Watch for external search mode changes (from TableOfContentToggle)
watch(() => props.searchMode, (newMode) => {
  searchModeRef.value = newMode ?? false
}, { immediate: true })

const { searchMode, searchTerm, isLoading, isError, results, hasNoResults } = useSearchUI(props.strategy, props.context, searchModeRef)

const searchInput = useTemplateRef<HTMLInputElement>('searchInput')

// Focus input when search mode is activated
watch(() => searchMode.value, (on) => {
  if (on) {
    nextTick(() => searchInput.value?.focus())
  }
})

// Handle result click
function handleResultClick(result: FormattedSearchResult) {
  // Clean highlights by extracting text from <span class="highlight"> tags
  const cleanedHighlights: string[] = result.highlights.flatMap((highlight) => {
    const matches = highlight.match(/<span class="highlight">(.*?)<\/span>/g) || []
    return matches.map(match => match.replace(/<span class="highlight">|<\/span>/g, ''))
  }).filter(h => h && h.trim().length > 0)

  emit('showSearchResult', result.id, cleanedHighlights)
}
</script>

<template>
  <div v-show="props.searchMode">
    <div class="sticky top-12 max-md:top-[52px] z-[8] pt-s max-md:pt-0 pb-m bg-surface-elevated flex flex-col gap-m">
      <form class="bg-inherit hidden md:flex" @submit.prevent>
        <FormSearchInput
          :id="`search-${context.contentType}`"
          ref="searchInput"
          v-model="searchTerm"
          shape="square"
          :placeholder="strategy.getSearchPlaceholder()"
          size="medium"
          name="q"
          color="carbon"
          resettable
        />
      </form>
      <div class="flex items-center gap-1 w-full" aria-live="polite" aria-atomic="true">
        <Skeleton v-if="isLoading" class="h-4 w-2/4" />
        <span v-else class="text-caption flex-1">
          <template v-if="results.length">Viser {{ results.length }} treff i {{ strategy.getContentTypeLabel().toLowerCase() }}</template>
          <template v-else-if="isError">Søket kunne ikke fullføres. Prøv igjen.</template>
          <template v-else-if="hasNoResults">Ingen treff</template>
        </span>
      </div>
    </div>

    <div v-if="searchTerm.length" class="flex flex-col gap-xs">
      <span class="sr-only">Søkeresultat</span>

      <!-- Loading state -->
      <template v-if="isLoading">
        <div v-for="n in 6" :key="n" class="hover:bg-surface-secondary hover:border-b-0 hover:rounded group border-b border-b-border-secondary">
          <div class="flex flex-col gap-2xs px-s pt-xs pb-s">
            <Skeleton class="h-4 rounded w-1/4" />
            <Skeleton class="h-6 rounded w-3/4" />
            <Skeleton class="h-4 rounded w-full" />
          </div>
        </div>
      </template>

      <!-- Results -->
      <template v-else>
        <template v-for="result in results" :key="result.id">
          <button
            class="group px-s pt-xs pb-s w-full hover:bg-surface-secondary hover:border-b-0 hover:rounded border-b-border-secondary border-b dark:border-b-border-default text-left"
            type="button"
            @click="handleResultClick(result)"
          >
            <div class="flex flex-col gap-2xs my-xs no-underline w-full">
              <p v-if="result.title" class="text-small" v-text="result.title" />
              <template v-if="result.highlights.length > 0">
                <span
                  v-for="(highlight, index) in result.highlights"
                  :key="index"
                  class="text-xsmall search-result-body"
                  v-html="highlight"
                />
              </template>
              <template v-else>
                <span class="text-xsmall line-clamp-2">{{ result.snippet }}</span>
              </template>
            </div>
          </button>
        </template>
      </template>
    </div>
  </div>
</template>
